import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MenuBar } from '../../components/ui/glow-menu';
import { StickyFooter } from '../../components/ui/sticky-footer';
import {
  Home,
  Video,
  ShieldCheck,
  Trophy,
  Radio,
  Search,
  UserCheck,
  History,
  AlertTriangle,
  BarChart3,
  Globe,
  ChevronRight,
} from 'lucide-react';
import logoImg from '../../assets/images/logo.svg';

const menuItems = [
  { icon: Home, label: 'Início', href: '/', gradient: 'radial-gradient(circle, rgba(245,138,37,0.2) 0%, transparent 100%)', iconColor: 'text-orange-500' },
  { icon: Video, label: 'Vídeos', href: '/videos', gradient: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 100%)', iconColor: 'text-blue-500' },
  { icon: Radio, label: 'Ao Vivo', href: '/livestream', gradient: 'radial-gradient(circle, rgba(239,68,68,0.15) 0%, transparent 100%)', iconColor: 'text-red-500' },
  { icon: ShieldCheck, label: 'FutSafe', href: '/futsafe', gradient: 'radial-gradient(circle, rgba(234,179,8,0.2) 0%, transparent 100%)', iconColor: 'text-yellow-500' },
  { icon: Trophy, label: 'Torneios', href: '/torneios', gradient: 'radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 100%)', iconColor: 'text-purple-500' },
];

const steps = [
  {
    icon: UserCheck,
    title: 'Cadastro Verificado',
    description: 'Organizadores criam fichas de atletas com CPF e email, garantindo identidade única no sistema.',
    color: '#EAB308',
  },
  {
    icon: Globe,
    title: 'Busca Cross-Arena',
    description: 'O sistema cruza dados de todas as arenas do ArenAi, identificando o mesmo atleta em diferentes torneios.',
    color: '#F59E0B',
  },
  {
    icon: History,
    title: 'Histórico Completo',
    description: 'Consulte todos os torneios disputados, categorias, colocações e estatísticas de qualquer atleta.',
    color: '#FACC15',
  },
];

const features = [
  {
    icon: Search,
    title: 'Busca de Atletas',
    description: 'Pesquise qualquer jogador pelo nome e veja seu perfil completo em todas as arenas.',
  },
  {
    icon: AlertTriangle,
    title: 'Detecção Fora de Categoria',
    description: 'Alertas automáticos quando um atleta compete em categorias abaixo do seu nível real.',
  },
  {
    icon: Trophy,
    title: 'Conquistas e Badges',
    description: 'Acompanhe títulos, vice-campeonatos e pódios de cada atleta ao longo da carreira.',
  },
  {
    icon: BarChart3,
    title: 'Estatísticas Detalhadas',
    description: 'ATK, DEF, SAQ, REC, BLQ, FIN — ratings completos como em jogos de esportes.',
  },
  {
    icon: Globe,
    title: 'Acesso Público',
    description: 'Qualquer pessoa pode consultar o histórico de atletas, sem necessidade de cadastro.',
  },
  {
    icon: ShieldCheck,
    title: 'Identidade Protegida',
    description: 'CPF e dados sensíveis nunca são exibidos publicamente. Segurança em primeiro lugar.',
  },
];

const categories = [
  { name: 'Aprendiz', tier: 'Bronze', color: '#CD7F32', card: '/fut-cards/large-rare-bronze.png' },
  { name: 'Iniciante C', tier: 'Prata', color: '#C0C0C0', card: '/fut-cards/large-rare-silver.png' },
  { name: 'Iniciante B', tier: 'Ouro', color: '#FFD700', card: '/fut-cards/large-rare-gold.png' },
  { name: 'Iniciante A', tier: 'Especial', color: '#FF8C00', card: '/fut-cards/large-if-gold.png' },
  { name: 'Intermediário', tier: 'Hero', color: '#9333EA', card: '/fut-cards/large-hero.png' },
  { name: 'Prata', tier: 'Lenda', color: '#3B82F6', card: '/fut-cards/large-legend.png' },
  { name: 'Open / Ouro', tier: 'TOTY', color: '#EF4444', card: '/fut-cards/large-toty.png' },
];

export default function FutSafe() {
  const navigate = useNavigate();

  const handleMenuClick = (label: string) => {
    const item = menuItems.find((i) => i.label === label);
    if (item) navigate(item.href);
  };

  return (
    <div className="flex flex-col min-h-screen" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Navbar */}
      <header
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-12 py-3 backdrop-blur-xl border-b border-white/5"
        style={{ backgroundColor: 'rgba(10,10,10,0.8)' }}
      >
        <img src={logoImg} alt="Apertai" className="hidden md:block" style={{ width: '127px' }} />
        <MenuBar items={menuItems} activeItem="FutSafe" onItemClick={handleMenuClick} />
      </header>

      {/* Hero */}
      <section className="relative pt-40 pb-20 px-4 overflow-hidden">
        {/* Warm gold ambient */}
        <div
          className="absolute inset-0 opacity-10"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, #EAB308, transparent 60%)' }}
        />
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div
              className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-8"
              style={{
                background: 'linear-gradient(135deg, #EAB308, #FACC15)',
                boxShadow: '0 4px 30px rgba(234, 179, 8, 0.4)',
              }}
            >
              <ShieldCheck className="w-10 h-10 text-black" />
            </div>
            <h1
              className="text-5xl md:text-7xl font-black tracking-tight mb-6"
              style={{
                backgroundImage: 'linear-gradient(135deg, #EAB308, #FACC15, #FDE68A)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              FutSafe
            </h1>
            <p className="text-xl md:text-2xl text-zinc-400 max-w-2xl mx-auto mb-4">
              O sistema de verificação e histórico de atletas do ecossistema ArenAi
            </p>
            <p className="text-base text-zinc-600 max-w-xl mx-auto mb-10">
              Consulte o histórico de qualquer atleta em todas as arenas. Transparência e segurança para o esporte.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://arenai.com.br/torneios-publicos"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg font-semibold text-black text-base no-underline transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #EAB308, #FACC15)', boxShadow: '0 4px 20px rgba(234,179,8,0.3)' }}
              >
                <Search className="w-5 h-5" />
                Consultar Atleta
              </a>
              <a
                href="https://arenai.com.br"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg font-semibold text-yellow-400 text-base border border-yellow-500/30 no-underline transition-all hover:border-yellow-400 hover:bg-yellow-500/5"
              >
                Conheça o ArenAi
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FIFA-style Cards Showcase */}
      <section className="py-16 px-4 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">Cards de Atleta</h2>
          <p className="text-zinc-500 text-center mb-12 max-w-lg mx-auto">
            Cada atleta possui um card único com estatísticas e categoria, inspirado nos jogos de esporte
          </p>
          {/* 3D Perspective showcase */}
          <div className="flex justify-center items-center gap-4 md:gap-8" style={{ perspective: '1200px' }}>
            {[
              { name: 'JOÃO SILVA', overall: 87, pos: 'ATK', bg: '/fut-cards/large-rare-gold.png', textColor: '#4a3b10', stats: [85, 72, 90, 78, 65, 88] },
              { name: 'CARLOS LIMA', overall: 92, pos: 'DEF', bg: '/fut-cards/large-toty.png', textColor: '#d4af37', stats: [94, 91, 88, 90, 93, 86] },
              { name: 'PEDRO COSTA', overall: 78, pos: 'FIN', bg: '/fut-cards/large-rare-silver.png', textColor: '#3a3a3a', stats: [80, 68, 75, 72, 60, 82] },
            ].map((card, i) => (
              <motion.div
                key={card.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                whileHover={{ scale: 1.08, rotateY: i === 0 ? -5 : i === 2 ? 5 : 0, z: 50 }}
                style={{
                  transformStyle: 'preserve-3d',
                  transform: i === 0 ? 'rotateY(15deg) scale(0.9)' : i === 2 ? 'rotateY(-15deg) scale(0.9)' : 'rotateY(0deg) scale(1.05)',
                  zIndex: i === 1 ? 10 : 1,
                }}
              >
                <div
                  style={{
                    width: i === 1 ? 220 : 185,
                    height: i === 1 ? 330 : 278,
                    position: 'relative',
                    backgroundImage: `url(${card.bg})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    fontFamily: "'Titillium Web', sans-serif",
                    filter: i === 1 ? 'drop-shadow(0 20px 40px rgba(212,175,55,0.3))' : 'drop-shadow(0 10px 20px rgba(0,0,0,0.4))',
                  }}
                >
                  {/* Overall */}
                  <div style={{ position: 'absolute', top: '17%', left: '16%', fontWeight: 900, color: card.textColor, lineHeight: 1 }}>
                    <div style={{ fontSize: i === 1 ? '1.8rem' : '1.5rem' }}>{card.overall}</div>
                    <div style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginTop: 2, textAlign: 'center' }}>{card.pos}</div>
                  </div>

                  {/* Player silhouette */}
                  <div style={{
                    position: 'absolute', top: '13%', left: '50%', transform: 'translateX(-50%)',
                    width: i === 1 ? 110 : 90, height: i === 1 ? 110 : 90,
                    display: 'flex', alignItems: 'flex-end', justifyContent: 'center', overflow: 'hidden',
                  }}>
                    <svg width={i === 1 ? 50 : 40} height={i === 1 ? 50 : 40} viewBox="0 0 24 24" fill="none" stroke={`${card.textColor}44`} strokeWidth="1.5" style={{ marginBottom: 10 }}>
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>

                  {/* Name */}
                  <div style={{
                    position: 'absolute', top: '50%', left: 0, right: 0,
                    textAlign: 'center', fontWeight: 800, fontSize: i === 1 ? '0.78rem' : '0.65rem',
                    color: card.textColor, letterSpacing: 0.5, textTransform: 'uppercase',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    padding: '0 20px',
                  }}>
                    {card.name}
                  </div>

                  {/* Divider */}
                  <div style={{ position: 'absolute', top: '56%', left: '15%', right: '15%', height: 1, background: `${card.textColor}44` }} />

                  {/* Stats */}
                  <div style={{
                    position: 'absolute', top: '59%', left: '12%', right: '12%',
                    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px 0',
                  }}>
                    {['ATK', 'DEF', 'SAQ', 'REC', 'BLQ', 'FIN'].map((label, si) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center', padding: '2px 0' }}>
                        <span style={{ fontSize: i === 1 ? '0.85rem' : '0.72rem', fontWeight: 800, color: card.textColor }}>{card.stats[si]}</span>
                        <span style={{ fontSize: '0.5rem', fontWeight: 600, color: `${card.textColor}99`, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works - Timeline */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">Como funciona</h2>
          <p className="text-zinc-500 text-center mb-16 max-w-lg mx-auto">
            Um processo simples em 3 etapas que garante a integridade dos torneios
          </p>
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 bottom-0 w-px bg-zinc-800 hidden md:block" />
            <div className="space-y-12">
              {steps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.15, duration: 0.5 }}
                    className="flex gap-6 items-start"
                  >
                    <div
                      className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center relative z-10"
                      style={{ backgroundColor: step.color, boxShadow: `0 0 20px ${step.color}40` }}
                    >
                      <Icon className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-zinc-600 uppercase">Passo {i + 1}</span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                      <p className="text-zinc-400 leading-relaxed max-w-md">{step.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Categories showcase */}
      <section className="py-20 px-4" style={{ background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1208 50%, #0a0a0a 100%)' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">Categorias de Atletas</h2>
          <p className="text-zinc-500 text-center mb-12 max-w-lg mx-auto">
            Sistema gamificado inspirado em cards de jogos esportivos
          </p>
          <div className="flex flex-wrap justify-center gap-6 md:gap-8">
            {categories.map((cat) => (
              <motion.div
                key={cat.name}
                whileHover={{ scale: 1.08, y: -8 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="flex flex-col items-center gap-3"
              >
                <div
                  style={{
                    width: 100,
                    height: 150,
                    backgroundImage: `url(${cat.card})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    filter: `drop-shadow(0 4px 12px ${cat.color}40)`,
                  }}
                />
                <div className="text-center">
                  <p className="text-white text-sm font-semibold">{cat.name}</p>
                  <p className="text-xs font-medium" style={{ color: cat.color }}>{cat.tier}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">Recursos</h2>
          <p className="text-zinc-500 text-center mb-12 max-w-lg mx-auto">
            Tudo que você precisa para consultar e verificar atletas
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feat, i) => {
              const Icon = feat.icon;
              return (
                <motion.div
                  key={feat.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.4 }}
                  className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:border-yellow-500/20 transition-colors"
                >
                  <Icon className="w-8 h-8 text-yellow-400 mb-4" />
                  <h3 className="text-lg font-bold text-white mb-2">{feat.title}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed">{feat.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div
            className="rounded-3xl p-12 md:p-16 border"
            style={{ borderColor: '#EAB30830', background: 'linear-gradient(135deg, #EAB30808, #1a1a1a, #EAB30808)' }}
          >
            <ShieldCheck className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Consulte agora
            </h2>
            <p className="text-zinc-400 mb-8 max-w-md mx-auto">
              Acesse o diretório público de torneios e busque qualquer atleta gratuitamente.
            </p>
            <a
              href="https://arenai.com.br/torneios-publicos"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-10 py-4 rounded-lg font-bold text-black text-base no-underline transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #EAB308, #FACC15)', boxShadow: '0 4px 30px rgba(234,179,8,0.3)' }}
            >
              <Search className="w-5 h-5" />
              Abrir FutSafe
            </a>
          </div>
        </div>
      </section>

      <StickyFooter />
    </div>
  );
}
